import { useCallback, useContext, useMemo } from 'react';
import FeaturesContext, { Features, FeaturesContextType } from 'context/FeaturesContext';
import { CheckFeatureFunction } from './types';

const getEnabledFeatureKeys = (features: Features): string[] =>
  Object.entries(features)
    .filter(([, isEnabled]) => isEnabled)
    .map(([key]) => key)
    .sort((a, b) => a.localeCompare(b));

// The third element is the hydration signal. Existing two-element call sites are unaffected; take it
// only when a disabled flag drives something irreversible (see FeaturesContextType.areFeaturesReady).
const useCheckFeatures = (): readonly [Features, (enabler: string) => boolean, boolean] => {
  const { features, areFeaturesReady } = useContext<FeaturesContextType>(FeaturesContext);

  // Preference broadcasts can replace `features` with a new object even when enabled keys are unchanged.
  // `useMemo` compares deps by reference, so derive a stable string signature first; only rebuild the Set
  // when that signature changes (not on every broadcast clone).
  const enabledKeysSignature = useMemo(
    () => getEnabledFeatureKeys(features).join(','),
    [features]
  );

  // Provides stability for the set of enabled features.
  const enabledFeatures = useMemo(
    () => new Set(enabledKeysSignature === '' ? [] : enabledKeysSignature.split(',')),
    [enabledKeysSignature]
  );

  const checkFeature: CheckFeatureFunction = useCallback(enabler => enabledFeatures.has(enabler), [enabledFeatures]);

  return [features, checkFeature, areFeaturesReady] as const;
};

export const useFeatureFlags = () => {
  const { features, toggleFeature } = useContext<FeaturesContextType>(FeaturesContext);

  return { features, toggleFeature };
};

export default useCheckFeatures;
